import React, { Component } from 'react';
import { AppProvider, Banner, Button, Page, Spinner, Stack } from '@shopify/polaris';
import { ApiError, getJson } from '../helpers/request';

interface AuthenticatedPageProps {
  children: (shopOrigin: string) => React.ReactNode;
}

interface AuthenticatedPageState {
  shopOrigin: string;
  error: string;
  requiresLogin: boolean;
}

class AuthenticatedPage extends Component<AuthenticatedPageProps, AuthenticatedPageState> {
  state: AuthenticatedPageState = {
    shopOrigin: '',
    error: '',
    requiresLogin: false,
  };

  private mounted = false;
  private requestId = 0;

  componentDidMount() {
    this.mounted = true;
    this.loadShop();
  }

  componentWillUnmount() {
    this.mounted = false;
    this.requestId += 1;
  }

  loadShop = async () => {
    const requestId = ++this.requestId;
    this.setState({ shopOrigin: '', error: '', requiresLogin: false });

    try {
      const data = await getJson('/app/api/shopify/user');
      if (!data || typeof data.shop_origin !== 'string' || !data.shop_origin.trim()) {
        throw new Error('The shop response was incomplete.');
      }
      if (this.mounted && requestId === this.requestId) {
        this.setState({ shopOrigin: data.shop_origin });
      }
    } catch (error) {
      if (!this.mounted || requestId !== this.requestId) return;
      const requiresLogin = error instanceof ApiError && (error.status === 401 || error.status === 403);
      this.setState({
        error: requiresLogin
          ? 'Your Shopify session needs to be reconnected.'
          : 'We could not load your shop. Please try again.',
        requiresLogin,
      });
    }
  };

  render() {
    const { shopOrigin, error, requiresLogin } = this.state;

    return (
      <AppProvider i18n={{}}>
        {shopOrigin ? this.props.children(shopOrigin) : (
          <Page>
            {error ? (
              <Banner title="Unable to load Advanced Collections" status="critical">
                <Stack vertical>
                  <p>{error}</p>
                  <Stack>
                    <Button onClick={this.loadShop}>Try again</Button>
                    {requiresLogin && <Button url={'/login' + window.location.search}>Reconnect to Shopify</Button>}
                  </Stack>
                </Stack>
              </Banner>
            ) : (
              <div role="status" aria-live="polite" style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner accessibilityLabel="Loading your shop" size="large" />
                <p>Loading Advanced Collections…</p>
              </div>
            )}
          </Page>
        )}
      </AppProvider>
    );
  }
}

export default AuthenticatedPage;
